import React, { useEffect, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import FormStepper from 'components/base/form-stepper';
import AddProcurementForm from './add-procurement-form';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import AddProcurementPreview from './add-procurement-preview';
import { AddProcurementType, ProcurementType } from 'models/procurement.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import ProcurementStatusTag from 'components/base/procurement-status-tag';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

export const vendorOptions = [
  { id: '1', name: 'Femite food ltd', price: 'N 1,700/gram' },
  { id: '2', name: 'Femite food ltd', price: 'N 1,700/gram' },
  { id: '3', name: 'Femite food ltd', price: 'N 1,700/gram' },
  { id: '4', name: 'Femite food ltd', price: 'N 1,700/gram' }
];

const initialProcurement = {
  vendor_id: '',
  ingredient_id: '',
  ingredient_name: '',
  vendor_ingredient_id: '',
  vendor_ingredient_price: '',
  vendor_ingredient_unit_type: '',
  quantity: ''
};

const NewProcurement = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const { setIsBackLink, setBackLinkTitle } = useDashboardContext();
  const [formData, setFormData] = useState<(AddProcurementType & { id?: string })[]>([
    initialProcurement
  ]);
  const [originalItemIds, setOriginalItemIds] = useState<string[]>([]);
  const { state } = useLocation();
  const [isCreatingProcurementRequest, setIsCreatingProcurementRequest] = useState(false);
  const router = useNavigate();
  const { activeStore } = useDashboardContext();
  const { can } = useRoleContext();
  const canWriteProcurement = can(InventoryModule.Procurement, RolePermission.Write);

  useEffect(() => {
    if (state?.mode === 'edit' && state?.procurement) {
      const procurement = state.procurement as ProcurementType;
      const fetchItemDetails = async () => {
        const itemsWithDetails = await Promise.all(
          procurement.items.map(async (item: any) => {
            let vendorIngredientId = '';
            let vendorIngredientUnitType = item.ingredient.unit_type;
            let vendorIngredientPrice = item.price;

            // Fetch vendor ingredients to find the correct ID
            if (item.ingredient.id && item.vendor.id) {
              try {
                const res = await apiService.get(
                  `vendors/ingredients?ingredient_id=${item.ingredient.id}`
                );
                if (res && res.data && res.data.results) {
                  const match = res.data.results.find((vi: any) => vi.vendor_id === item.vendor.id);
                  if (match) {
                    vendorIngredientId = match.id;
                    vendorIngredientUnitType = match.ingredient.unit_type;
                    vendorIngredientPrice =
                      '₦ ' + Number(match.gram_price || match.price).toLocaleString();
                  }
                }
              } catch (e) {
                console.error('Failed to fetch vendor ingredients', e);
              }
            }

            return {
              id: item.id,
              vendor_id: item.vendor.id,
              ingredient_id: item.ingredient.id,
              ingredient_name: item.ingredient.name,
              vendor_ingredient_id: vendorIngredientId,
              vendor_ingredient_price: String(vendorIngredientPrice),
              vendor_ingredient_unit_type: vendorIngredientUnitType,
              quantity: String(item.quantity)
            };
          })
        );
        setFormData(itemsWithDetails);
        setOriginalItemIds(procurement.items.map((i) => i.id));
      };

      fetchItemDetails();
    }
  }, [state]);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to procurement');
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  const setValue = (index: number, field: keyof AddProcurementType, value: string) => {
    setFormData((prev) =>
      prev.map((data, i) => (i === index ? { ...data, [field]: value } : data))
    );
  };

  const addForm = () => setFormData([...formData, { ...initialProcurement }]);

  const removeItem = (index: number) => setFormData(formData.filter((data, i) => i !== index));

  const createProcurementRequest = async () => {
    if (!canWriteProcurement) return;
    if (!activeStore?.id) return;

    if (state?.mode === 'edit' && state?.procurement) {
      const data = {
        idsToDelete: originalItemIds,
        note: state.procurement.note || '.',
        title: state.procurement.title || '.',
        new_items: formData.map((request) => ({
          vendor_id: request.vendor_id,
          vendor_ingredient_id: request.vendor_ingredient_id,
          ingredient_id: request.ingredient_id,
          quantity: Number(request.quantity)
        }))
      };

      try {
        setIsCreatingProcurementRequest(true);
        const response = await apiService.put(`procurement/${state.procurement.id}`, {
          data,
          version: 'v2'
        });
        if (response) {
          router(MAIN_STORE_ROUTE_KEYS.PROCUREMENT);
          toast(<ToastMessage message='Procurement request updated successfully' type='success' />);
        }
      } catch (err: any) {
        toast(
          <ToastMessage
            message={err.response?.data?.message || 'Error updating procurement request'}
            type='error'
          />
        );
      } finally {
        setIsCreatingProcurementRequest(false);
      }
      return;
    }

    const data = {
      title: '.',
      note: '.',
      store_id: activeStore.id,
      items: formData.map((request) => {
        return {
          vendor_id: request.vendor_id,
          vendor_ingredient_id: request.vendor_ingredient_id,
          ingredient_id: request.ingredient_id,
          quantity: Number(request.quantity)
        };
      })
    };

    try {
      setIsCreatingProcurementRequest(true);
      const response = await apiService.post('procurement', {
        data,
        version: 'v2'
      });
      if (response) {
        router(MAIN_STORE_ROUTE_KEYS.PROCUREMENT);
        toast(<ToastMessage message='Procurement request created successfully' type='success' />);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error creating procurement request'}
          type='error'
        />
      );
    } finally {
      setIsCreatingProcurementRequest(false);
    }
  };

  const handleEdit = () => setStep(1);
  return (
    <div className='ml-2 md:ml-8'>
      {state?.mode === 'edit' && state?.procurement ? (
        <div className='flex items-center gap-2 mb-2'>
          <h4 className='text-[24px] font-semibold text-[#2E3031]'>
            Edit Procurement Request #{state.procurement.code}
          </h4>
          <ProcurementStatusTag type={state.procurement.status} />
        </div>
      ) : (
        <h4 className='text-[24px] font-semibold text-[#2E3031]'>Create Procurement Request</h4>
      )}
      <p className='text-[#6F7376] text-sm mb-8'>
        Inventory running low on certain items? prepare your list
      </p>
      <div className=' w-full max-w-[678px]'>
        <FormStepper
          disabled={false}
          hidePrimaryAction={!canWriteProcurement}
          step={step}
          title={step === 1 ? 'Make your selections' : 'Review selections'}
          subtitle={step === 2 ? 'Ensure you have all items needed selected' : ''}
          setStep={setStep}
          handleConfirmClick={() => {
            createProcurementRequest();
          }}
          loading={isCreatingProcurementRequest}
          handleCancelClick={() => {
            navigate(-1);
          }}>
          {step === 1 ? (
            <div>
              {formData.map((data, index) => (
                <AddProcurementForm
                  key={index}
                  data={data}
                  index={index}
                  setValue={setValue}
                  removeItem={removeItem}
                  totalItems={formData.length}
                  canWrite={canWriteProcurement}
                />
              ))}
              {canWriteProcurement && (
                <button onClick={addForm} className='text-[#0080E5] mt-2 font-medium text-sm'>
                  + Add Item
                </button>
              )}
            </div>
          ) : (
            <div>
              <AddProcurementPreview formData={formData} handleEdit={handleEdit} />
            </div>
          )}
        </FormStepper>
      </div>
    </div>
  );
};

export default NewProcurement;
